import React, { useState } from 'react'
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  ChevronDownIcon,
  PaperAirplaneIcon,
  ArrowUpRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import { motion, AnimatePresence } from 'framer-motion'
import { validateEmail, validateFullName, validatePhone } from '../../utils/validation'
import { contactApi } from '../../services/api'

const PHONE = '+91 9382093168'
const EMAIL = 'rahul@analyzerview.com'
const ADDRESS = 'Purbasthali, Purba Barddhaman, West Bengal 713513'
const MAPS_URL = 'https://maps.google.com/maps?q=Purbasthali%2C%20Purba%20Barddhaman%2C%20West%20Bengal%20713513'

const contactChannels = [
  {
    icon: PhoneIcon,
    label: 'Call us',
    value: PHONE,
    hint: 'Mon – Sat, business hours',
    href: `tel:${PHONE.replace(/\s/g, '')}`,
    accent: 'text-primary-400',
    ring: 'from-primary-500/20 to-primary-500/5'
  },
  {
    icon: EnvelopeIcon,
    label: 'Email us',
    value: EMAIL,
    hint: 'Replies within 24 hours',
    href: `mailto:${EMAIL}`,
    accent: 'text-success-400',
    ring: 'from-success-500/20 to-success-500/5'
  },
  {
    icon: MapPinIcon,
    label: 'Visit us',
    value: ADDRESS,
    hint: 'Open in Google Maps',
    href: MAPS_URL,
    external: true,
    accent: 'text-purple-400',
    ring: 'from-purple-500/20 to-purple-500/5'
  }
]

const businessHours = [
  { days: 'Monday – Friday', hours: '9:00 AM – 6:00 PM' },
  { days: 'Saturday', hours: '9:00 AM – 2:00 PM' },
  { days: 'Sunday', hours: 'Closed' }
]

const faqs = [
  {
    question: 'How do I get started?',
    answer:
      "Simply register for an account and choose a subscription plan that suits your needs. You'll get immediate access to our recommendations and analysis."
  },
  {
    question: 'What is your success rate?',
    answer:
      "Our recommendations are based on thorough research and analysis. However, past performance doesn't guarantee future results as the stock market is subject to risks."
  },
  {
    question: 'Do you provide guaranteed returns?',
    answer:
      "No, we don't guarantee returns as the stock market is subject to risks. We provide research-based recommendations to help you make informed decisions."
  },
  {
    question: 'How do I receive alerts?',
    answer:
      "Depending on your subscription plan, you'll receive alerts via email, SMS, and WhatsApp. You can also check notifications in your dashboard."
  }
]

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 }
  }
}

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } }
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  })
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0)
  const [errors, setErrors] = useState<Record<string, string>>({})

  type ContactForm = typeof formData

  const normalizeFieldValue = (field: keyof ContactForm, rawValue: string) => {
    if (field === 'phone') return rawValue.replace(/\D/g, '').slice(0, 10)
    return rawValue
  }

  const validateField = (field: keyof ContactForm, value: string) => {
    if (field === 'name') {
      if (!value.trim() || !validateFullName(value)) return 'Please enter a valid full name.'
      return ''
    }
    if (field === 'email') {
      if (!value.trim() || !validateEmail(value)) return 'Please enter a valid email address.'
      return ''
    }
    if (field === 'phone') {
      if (value.trim() && !validatePhone(value)) return 'Please enter a valid 10-digit mobile number.'
      return ''
    }
    if (field === 'subject') {
      if (!value.trim()) return 'Subject is required.'
      return ''
    }
    if (field === 'message') {
      if (!value.trim()) return 'Message is required.'
      return ''
    }
    return ''
  }

  const validateForm = (values = formData) => {
    const nextErrors: Record<string, string> = {}
    ;(['name', 'email', 'phone', 'subject', 'message'] as (keyof ContactForm)[]).forEach((field) => {
      const fieldError = validateField(field, values[field])
      if (fieldError) nextErrors[field] = fieldError
    })
    return nextErrors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const nextErrors = validateForm()
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return
    setLoading(true)
    setSubmitError('')

    try {
      await contactApi.submit(formData)
      setSubmitted(true)
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      })
    } catch (err: any) {
      setSubmitError(err.response?.data?.detail || 'Failed to send your message. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    const field = name as keyof ContactForm
    const nextValue = normalizeFieldValue(field, value)
    setFormData(prev => ({ ...prev, [field]: nextValue }))
    setErrors(prevErrors => {
      const next = { ...prevErrors }
      const fieldError = validateField(field, nextValue)
      if (fieldError) next[field] = fieldError
      else delete next[field]
      return next
    })
  }

  const handleBlur = (field: keyof ContactForm) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = normalizeFieldValue(field, e.target.value)
    setErrors(prevErrors => {
      const next = { ...prevErrors }
      const fieldError = validateField(field, value)
      if (fieldError) next[field] = fieldError
      else delete next[field]
      return next
    })
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-dark-950 text-white">
      {/* Ambient background */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 left-1/2 h-[28rem] w-[44rem] -translate-x-1/2 rounded-full bg-primary-600/15 blur-3xl"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 -right-32 h-96 w-96 rounded-full bg-purple-600/10 blur-3xl"
          animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(37,99,235,0.06),transparent_30%)]" />
        <div className="absolute inset-0 [background-image:linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_0%,black,transparent)]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 pb-20 pt-7 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto mb-10 max-w-2xl text-center"
        >
          <h1 className="bg-gradient-to-r from-white via-primary-100 to-primary-300 bg-clip-text text-3xl font-bold tracking-tight text-transparent sm:text-4xl">
            Let's talk about your investments
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm text-dark-300 sm:text-base">
            Questions about our research, plans, or alerts? Reach out — we typically respond within 24 hours.
          </p>
        </motion.div>

        {/* Main grid: form leads, info rail on the right */}
        <div className="grid items-start gap-6 lg:grid-cols-5">
          {/* Contact form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
            className="lg:col-span-3"
          >
            <div className="relative overflow-hidden rounded-2xl border border-dark-700 bg-dark-900/70 p-6 shadow-xl backdrop-blur sm:p-8">
              <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-500/60 to-transparent" />
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="py-14 text-center"
                    role="status"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
                      className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-500/15 ring-1 ring-success-500/30"
                    >
                      <CheckCircleIcon className="h-9 w-9 text-success-400" aria-hidden="true" focusable="false" />
                    </motion.div>
                    <h2 className="mb-2 text-2xl font-bold text-white">Message Sent!</h2>
                    <p className="mb-6 text-dark-300">
                      Thank you for reaching out. We'll get back to you within 24 hours.
                    </p>
                    <Button onClick={() => setSubmitted(false)}>Send Another Message</Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className="mb-6 flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/15 ring-1 ring-inset ring-primary-500/30">
                        <PaperAirplaneIcon className="h-5 w-5 -rotate-45 text-primary-400" aria-hidden="true" focusable="false" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold leading-tight text-white">Send us a message</h2>
                        <p className="text-sm text-dark-400">Fill in the details and we'll be in touch.</p>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      {submitError && (
                        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                          {submitError}
                        </div>
                      )}
                      <div className="grid gap-5 md:grid-cols-2">
                        <Input
                          label="Full Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          onBlur={handleBlur('name')}
                          error={errors.name}
                          placeholder="Enter your full name"
                          required
                        />
                        <Input
                          label="Email Address"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur('email')}
                          error={errors.email}
                          placeholder="Enter your email"
                          required
                        />
                      </div>

                      <div className="grid gap-5 md:grid-cols-2">
                        <Input
                          label="Phone Number"
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          onBlur={handleBlur('phone')}
                          error={errors.phone}
                          placeholder="9876543210"
                        />
                        <Input
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          onBlur={handleBlur('subject')}
                          error={errors.subject}
                          placeholder="What is this regarding?"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="contact-message" className="block text-sm font-medium text-gray-200">
                          Message
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          onBlur={handleBlur('message')}
                          rows={5}
                          placeholder="Tell us more about your query..."
                          className={`w-full rounded-md border bg-dark-800 px-3 py-2 text-gray-100 placeholder-gray-400 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.message ? 'border-red-400 focus:ring-red-500' : 'border-dark-600 focus:border-primary-500'}`}
                          required
                          aria-required="true"
                        />
                        {errors.message && <p className="text-sm text-red-400">{errors.message}</p>}
                      </div>

                      <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-dark-400">
                          We'll never share your details with anyone else.
                        </p>
                        <Button type="submit" loading={loading} size="lg" className="w-full sm:w-auto">
                          {loading ? 'Sending...' : 'Send Message'}
                          {!loading && <PaperAirplaneIcon className="ml-2 h-4 w-4 -rotate-45" aria-hidden="true" focusable="false" />}
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Info rail */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="flex flex-col gap-4 lg:col-span-2"
          >
            {/* Contact channels */}
            <motion.div variants={item} className="overflow-hidden rounded-2xl border border-dark-700 bg-dark-900/70 shadow-lg backdrop-blur">
              <div className="divide-y divide-dark-800">
                {contactChannels.map(({ icon: Icon, label, value, hint, href, external, accent, ring }) => (
                  <a
                    key={label}
                    href={href}
                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="group flex items-center gap-3 p-4 transition-colors hover:bg-dark-800/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                  >
                    <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${ring} ring-1 ring-inset ring-white/10`}>
                      <Icon className={`h-5 w-5 ${accent}`} aria-hidden="true" focusable="false" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-dark-400">{label}</h3>
                      <p className="truncate text-sm text-white">{value}</p>
                      <p className="text-xs text-dark-400">{hint}</p>
                    </div>
                    <ArrowUpRightIcon
                      className="h-4 w-4 flex-shrink-0 text-dark-500 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary-400"
                      aria-hidden="true"
                      focusable="false"
                    />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Business hours */}
            <motion.div variants={item} className="rounded-2xl border border-dark-700 bg-dark-900/70 p-4 shadow-lg backdrop-blur">
              <div className="mb-3 flex items-center gap-2">
                <ClockIcon className="h-4 w-4 text-orange-400" aria-hidden="true" focusable="false" />
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-dark-400">Business Hours</h3>
              </div>
              <dl className="space-y-2">
                {businessHours.map(({ days, hours }) => (
                  <div key={days} className="flex items-center justify-between gap-4 text-sm">
                    <dt className="text-dark-300">{days}</dt>
                    <dd className={hours === 'Closed' ? 'font-medium text-dark-500' : 'font-medium text-white'}>{hours}</dd>
                  </div>
                ))}
              </dl>
            </motion.div>

            {/* Geo map */}
            <motion.div
              variants={item}
              className="group relative min-h-[180px] flex-1 overflow-hidden rounded-2xl border border-dark-700 shadow-lg"
            >
              <iframe
                title="AnalyzerView office location on the map"
                src={`${MAPS_URL}&z=12&output=embed`}
                className="absolute inset-0 h-full w-full border-0 [filter:invert(0.92)_hue-rotate(180deg)_brightness(0.95)_contrast(0.9)]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10" />
              <div className="pointer-events-none absolute inset-x-3 bottom-3 flex items-center gap-2 rounded-lg border border-dark-700 bg-dark-900/85 px-3 py-2 shadow-lg backdrop-blur">
                <MapPinIcon className="h-4 w-4 flex-shrink-0 text-primary-400" aria-hidden="true" focusable="false" />
                <span className="truncate text-xs text-dark-200">{ADDRESS}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* FAQ section */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mt-20"
        >
          <div className="mb-8 text-center">
            <span className="inline-flex items-center rounded-full border border-dark-700 bg-dark-900/70 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-dark-300">
              FAQ
            </span>
            <h2 className="mt-3 text-2xl font-bold text-white sm:text-3xl">Frequently Asked Questions</h2>
            <p className="mt-2 text-dark-400">Quick answers to what people usually ask.</p>
          </div>

          <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-dark-700 bg-dark-900/70 shadow-lg backdrop-blur">
            <div className="divide-y divide-dark-800">
              {faqs.map((faq, index) => {
                const isOpen = openFaqIndex === index
                return (
                  <div key={index}>
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                      aria-expanded={isOpen}
                      aria-controls={`faq-panel-${index}`}
                      id={`faq-button-${index}`}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-dark-800/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                    >
                      <h3 className={`text-base font-semibold transition-colors ${isOpen ? 'text-primary-300' : 'text-white'}`}>
                        {faq.question}
                      </h3>
                      <ChevronDownIcon
                        aria-hidden="true"
                        focusable="false"
                        className={`h-5 w-5 flex-shrink-0 text-primary-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          id={`faq-panel-${index}`}
                          role="region"
                          aria-labelledby={`faq-button-${index}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <p className="px-5 pb-4 text-sm leading-relaxed text-dark-300">{faq.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </div>
          </div>
        </motion.div>

        {/* Closing CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="relative mt-16 overflow-hidden rounded-2xl border border-primary-500/30 bg-gradient-to-br from-primary-600/20 via-dark-900/70 to-purple-600/10 p-8 text-center shadow-xl backdrop-blur sm:p-10"
        >
          <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/60 to-transparent" />
          <h2 className="text-xl font-bold text-white sm:text-2xl">Still have questions?</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-dark-300">
            Can't find what you're looking for? Drop us an email and our team will get back to you within a business day.
          </p>
          <a href={`mailto:${EMAIL}`} className="mt-5 inline-block">
            <Button size="lg">
              <EnvelopeIcon className="mr-2 h-5 w-5" aria-hidden="true" focusable="false" />
              Email {EMAIL}
            </Button>
          </a>
        </motion.div>
      </div>
    </div>
  )
}

export default ContactPage
